/**
 * Unsaved changes: nothing open is lost without a question (DESIGN.md §10).
 *
 * The 2026-10-03 inventory found no guard anywhere (`beforeunload`,
 * `useBlocker` and `usePrompt` all absent) while the dashboard's tabs render
 * conditionally, so switching tab unmounted an open group and dropped its
 * edits. A group with changes registers here while it is open; the dashboard
 * asks `confirmLeave()` before it switches tab, and the browser asks before
 * the page closes. The question itself is drawn by UnsavedChangesDialog.
 */

const open = new Map<number, string>();
let nextId = 1;
let asker: ((labels: string[]) => Promise<boolean>) | null = null;

function onBeforeUnload(event: BeforeUnloadEvent) {
  event.preventDefault();
  // Legacy browsers read the return value; modern ones show their own text.
  event.returnValue = '';
}

function syncPageGuard() {
  if (typeof window === 'undefined') return;
  if (open.size > 0) window.addEventListener('beforeunload', onBeforeUnload);
  else window.removeEventListener('beforeunload', onBeforeUnload);
}

/** Mark `label` as holding unsaved changes; call the returned function to clear it. */
export function holdUnsavedChanges(label: string): () => void {
  const id = nextId++;
  open.set(id, label);
  syncPageGuard();
  return () => {
    open.delete(id);
    syncPageGuard();
  };
}

/** The labels of every open group with changes, in the order they were opened. */
export function unsavedLabels(): string[] {
  return [...open.values()];
}

/** UnsavedChangesDialog installs the question; tests may install their own. */
export function setLeaveAsker(fn: ((labels: string[]) => Promise<boolean>) | null) {
  asker = fn;
}

let asking = 0;

/**
 * True while "Leave without saving?" is on screen. An editor that saves when
 * focus leaves it must not save then: the dialog takes the focus, and the
 * question says the change will be lost, so the answer decides, not the blur.
 */
export function isAskingToLeave(): boolean {
  return asking > 0;
}

/** True when nothing is open, or the person chose to leave without saving. */
export async function confirmLeave(): Promise<boolean> {
  const labels = unsavedLabels();
  if (labels.length === 0) return true;
  if (!asker) return true;
  asking++;
  try {
    return await asker(labels);
  } finally {
    asking--;
  }
}
